import { ChevronDownIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import {
    ActivityCategory,
    activityCategoryLabels,
    ActivityRecordType,
    NoteId,
    ReferenceBusinessId,
    ReferenceContactId,
    TaskTypeId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { InputFormBlock } from "components/app/input-form-block";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { Header } from "components/header";
import { useAgendaRefresh } from "components/marketing-plan/use-agenda-refresh";
import { NotesInput } from "components/notes/notes-input";
import { trpc } from "components/rpc/trpc";
import { activityCategoryIcons } from "components/tasks/activity-category-icons";
import {
    ActivityTypeOption,
    useActivityTypeOptions,
} from "components/tasks/activity-type-options";
import { TouchpointTypeCell } from "components/tasks/touchpoint-type-cell";
import { TaskFormV2 } from "components/tasks/v2/task-form-v2";
import { Taskv2 } from "components/tasks/v2/types";
import { BlankSlate } from "components/ui/blank-slate";
import { ButtonSelectOption } from "components/ui/button-select";
import { ReferenceBusinessLink, ReferenceContactLink } from "components/ui/entity-link";
import { FilterChipRow } from "components/ui/filter-chip-row";
import { InteractiveTextLink } from "components/ui/interactive-text-link";
import { ArrivingListItem, useArrivals } from "components/ui/list-motion";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { PrimaryPlusButton } from "components/ui/primary-plus-button";
import { parentVisitsQueryOptions } from "components/visits/parent-visits";
import {
    ActivityParent,
    activityRecordTypeForActivityParentKind,
    CalendarEventDto,
    visitParentFor,
} from "components/visits/types";
import { VisitEditor } from "components/visits/visit-editor";
import { AnimatePresence } from "framer-motion";
import {
    GetNotesForEntityQuery,
    GetTasksForEntityQuery,
    UpdateNoteMutation,
    useDeleteTaskMutation,
    useGetNotesForEntityQuery,
    useGetTasksForEntityQuery,
    useUpdateTaskMutation,
} from "generated/graphql";
import { produce } from "immer";
import React from "react";
import { useQueryClient } from "react-query";
import { activityCategoriesForParent, taskEntityForParent } from "./activity-parent";
import {
    ActivityFeedRowBody,
    ActivityRowEditButton,
    ActivityRowsList,
} from "./activity-rows";
import { ExpandableEmailBody } from "./expandable-email-body";
import { LogActivityWizard } from "./log-activity-wizard";
import {
    bylineMemberContact,
    filterRecentActivityItems,
    NOTES_FILTER_KEY,
    NOTES_TYPE_LABEL,
    RecentActivityFilterKey,
    recentActivityItemDisplay,
    sortRecentActivityItems,
} from "./recent-activities-feed";
import { RecentActivityNoteRow } from "./recent-activity-note-row";

const INITIAL_VISIBLE_COUNT = 3;
const SHOW_MORE_INCREMENT = 10;

type TaskDto = GetTasksForEntityQuery["getTasksForEntity"][0];

type FeedItem = inferProcedureOutput<AppRouter["recentActivity"]["forEntity"]>[number];

interface FeedItemRowProps {
    item: FeedItem;
    parent: ActivityParent;
    onEdit: (() => void) | null;
    recordType: ActivityRecordType;
    activityTypes: ActivityTypeOption[];
    onTypeSaved: () => Promise<void>;
}

const touchpointBusinessIdFor = (
    item: FeedItem,
    parent: ActivityParent,
): ReferenceBusinessId | null => {
    if (item.taggedOnBusiness) {
        return item.taggedOnBusiness.businessId;
    }
    if (parent.kind === "organization" && !item.memberContact) {
        return parent.id as ReferenceBusinessId;
    }
    return null;
};

const FeedItemRow: React.FC<FeedItemRowProps> = props => {
    const { item, parent, onEdit, recordType, activityTypes, onTypeSaved } = props;

    const businessId = touchpointBusinessIdFor(item, parent);

    const actions = onEdit ? (
        <ActivityRowEditButton onClick={onEdit} />
    ) : item.kind === "touchpoint" && item.touchpointId ? (
        <TouchpointTypeCell
            target={{
                touchpointId: item.touchpointId,
                category: item.category ?? "other",
                taskTypeId: (item.taskTypeId as TaskTypeId | null) ?? null,
                note: item.note ?? null,
                involvedContactIds: item.involvedContactIds as ReferenceContactId[],
                businessId,
            }}
            recordType={recordType}
            activityTypes={activityTypes}
            onSaved={onTypeSaved}
        />
    ) : null;

    const display = recentActivityItemDisplay(item);
    const memberBylineContact = bylineMemberContact(item);

    return (
        <ActivityFeedRowBody
            display={{
                ...display,
                expandableBody:
                    item.hasFullEmail && item.touchpointId ? (
                        <ExpandableEmailBody
                            touchpointId={item.touchpointId}
                            preview={item.text}
                        />
                    ) : null,
                link: memberBylineContact ? (
                    <ReferenceContactLink
                        referenceContactId={memberBylineContact.contactId}
                        name={memberBylineContact.name}
                    />
                ) : item.taggedOnBusiness ? (
                    <ReferenceBusinessLink
                        referenceBusinessId={item.taggedOnBusiness.businessId}
                        name={item.taggedOnBusiness.name}
                    />
                ) : null,
            }}
            actions={actions}
        />
    );
};

const feedFilterOptions = (
    categories: readonly ActivityCategory[],
): ButtonSelectOption<RecentActivityFilterKey>[] => [
    { id: NOTES_FILTER_KEY, label: NOTES_TYPE_LABEL, icon: DocumentTextIcon },
    ...categories.map(category => ({
        id: category,
        label: activityCategoryLabels[category],
        icon: activityCategoryIcons[category],
    })),
];

interface ActivityFilterChipsProps {
    categories: readonly ActivityCategory[];
    selected: RecentActivityFilterKey[];
    onToggle: (key: RecentActivityFilterKey) => void;
    onClear: () => void;
}

export const ActivityFilterChips: React.FC<ActivityFilterChipsProps> = props => {
    const { categories, selected, onToggle, onClear } = props;
    const options = React.useMemo(() => feedFilterOptions(categories), [categories]);
    return (
        <FilterChipRow
            label="Filter:"
            options={options}
            selectedIds={selected}
            onToggle={onToggle}
            onClear={onClear}
            trailing={null}
        />
    );
};

interface RecentActivitiesCardProps {
    parent: ActivityParent;
}

export const RecentActivitiesCard: React.FC<RecentActivitiesCardProps> = props => {
    const { parent } = props;
    const entityId = parent.id;
    const notesQuery = useGetNotesForEntityQuery({ entityId });
    const trpcClient = trpc.useTRPC();
    const feedQuery = useQuery(
        trpcClient.recentActivity.forEntity.queryOptions({ entityId }),
    );
    const visitParent = visitParentFor(parent);
    const feedCategories = React.useMemo(
        () => activityCategoriesForParent(parent),
        [parent],
    );
    const visitsQueryOptions = React.useMemo(
        () => parentVisitsQueryOptions(trpcClient, parent),
        [trpcClient, parent],
    );
    const visitsQuery = useQuery(visitsQueryOptions);
    const activityTypes = useActivityTypeOptions();
    const tasksQuery = useGetTasksForEntityQuery({ parentId: entityId });
    const updateTask = useUpdateTaskMutation();
    const deleteTask = useDeleteTaskMutation();
    const refreshAgenda = useAgendaRefresh();
    const queryClient = useQueryClient();
    const [localDeletes, setLocalDeletes] = React.useState<NoteId[]>([]);
    const [selectedTypes, setSelectedTypes] = React.useState<RecentActivityFilterKey[]>(
        [],
    );
    const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_COUNT);
    const [loggingActivity, setLoggingActivity] = React.useState(false);
    const [editingTask, setEditingTask] = React.useState<TaskDto | null>(null);
    const [editingVisit, setEditingVisit] = React.useState<CalendarEventDto | null>(null);
    const feedItemIds = React.useMemo(
        () => (feedQuery.data ? feedQuery.data.map(item => item.id) : null),
        [feedQuery.data],
    );
    const arrivals = useArrivals(feedItemIds);

    const closeAction = React.useCallback(() => setLoggingActivity(false), []);

    const closeEditors = React.useCallback(() => {
        setEditingTask(null);
        setEditingVisit(null);
    }, []);

    const tasksById = React.useMemo(
        () =>
            new Map<string, TaskDto>(
                (tasksQuery.data?.getTasksForEntity ?? []).map(task => [task.id, task]),
            ),
        [tasksQuery.data],
    );

    const visitsById = React.useMemo(
        () =>
            new Map<string, CalendarEventDto>(
                (visitsQuery.data ?? []).map(visit => [visit.id, visit]),
            ),
        [visitsQuery.data],
    );

    const editingInitial: Taskv2 | null = React.useMemo(() => {
        if (!editingTask) {
            return null;
        }
        return {
            entity: taskEntityForParent(parent),
            task: {
                __typename: "Task" as const,
                ...editingTask,
            },
        };
    }, [editingTask, parent]);

    const refetch = React.useCallback(() => {
        notesQuery.refetch();
        feedQuery.refetch();
        if (visitParent) {
            visitsQuery.refetch();
        }
    }, [notesQuery, feedQuery, visitsQuery, visitParent]);

    const feedRecordType: ActivityRecordType = activityRecordTypeForActivityParentKind(
        parent.kind,
    );

    const handleTypeSaved = React.useCallback(async () => {
        await feedQuery.refetch();
        await refreshAgenda();
    }, [feedQuery, refreshAgenda]);

    const toggleType = React.useCallback((key: RecentActivityFilterKey) => {
        setSelectedTypes(current =>
            current.includes(key)
                ? current.filter(existing => existing !== key)
                : [...current, key],
        );
        setVisibleCount(INITIAL_VISIBLE_COUNT);
    }, []);

    const clearTypes = React.useCallback(() => {
        setSelectedTypes([]);
        setVisibleCount(INITIAL_VISIBLE_COUNT);
    }, []);

    const handleNoteChanged = React.useCallback(
        (note: GetNotesForEntityQuery["getNotesForEntity"][0]) => {
            queryClient.setQueryData<GetNotesForEntityQuery>(
                ["getNotesForEntity", { entityId }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        let replaced = false;
                        draft.getNotesForEntity = draft.getNotesForEntity.map(n => {
                            if (n._id === note._id) {
                                replaced = true;
                                return note;
                            }
                            return n;
                        });
                        if (!replaced) {
                            draft.getNotesForEntity.unshift(note);
                        }
                    });
                },
            );
            feedQuery.refetch();
            if (!note.isDraft) {
                refreshAgenda();
            }
        },
        [queryClient, entityId, feedQuery, refreshAgenda],
    );

    const handleNoteDeleted = React.useCallback(
        (noteId: NoteId) => {
            setLocalDeletes(d => [...d, noteId]);
            refreshAgenda();
        },
        [refreshAgenda],
    );

    const handleNoteUpdated = React.useCallback(
        (note: UpdateNoteMutation["updateNote"]) => {
            queryClient.setQueryData<GetNotesForEntityQuery>(
                ["getNotesForEntity", { entityId }],
                data => {
                    if (!data) return data;
                    return produce(data, draft => {
                        draft.getNotesForEntity = draft.getNotesForEntity.map(n => {
                            if (n._id === note._id) {
                                return note;
                            }
                            return n;
                        });
                    });
                },
            );
            feedQuery.refetch();
            refreshAgenda();
        },
        [queryClient, entityId, feedQuery, refreshAgenda],
    );

    return (
        <QueryRenderer name="RecentActivities" query={notesQuery}>
            {notesData => {
                const allNotes = notesData.getNotesForEntity;
                const notesById = new Map(allNotes.map(note => [note._id, note]));
                const pinnedNoteId =
                    allNotes.find(
                        n => n.isPinned && !n.isDraft && !localDeletes.includes(n._id),
                    )?._id ?? null;

                const items = sortRecentActivityItems(feedQuery.data ?? []).filter(
                    item =>
                        !(
                            item.kind === "note" &&
                            localDeletes.includes(item.id as NoteId)
                        ),
                );
                const filtered = filterRecentActivityItems(items, selectedTypes);
                const visible = filtered.slice(0, visibleCount);
                const hasMore = filtered.length > visibleCount;
                const reachedEnd = !hasMore && visibleCount > INITIAL_VISIBLE_COUNT;

                return (
                    <InputFormBlock>
                        <Header
                            action={
                                <PrimaryPlusButton
                                    onClick={() => setLoggingActivity(true)}>
                                    Log Activity
                                </PrimaryPlusButton>
                            }>
                            Recent Activities
                        </Header>
                        <ActivityFilterChips
                            categories={feedCategories}
                            selected={selectedTypes}
                            onToggle={toggleType}
                            onClear={clearTypes}
                        />
                        <NotesInput
                            refetch={refetch}
                            entityId={entityId}
                            initial={allNotes}
                            pinnedNoteId={pinnedNoteId}
                            onNoteChanged={handleNoteChanged}
                        />
                        <ActivityRowsList
                            className="mt-6"
                            testId="recent-activities-feed">
                            <AnimatePresence initial={false}>
                                {visible.map(item => {
                                    const note =
                                        item.kind === "note"
                                            ? (notesById.get(item.id as NoteId) ?? null)
                                            : null;
                                    const editableTask =
                                        item.kind === "task"
                                            ? (tasksById.get(item.id) ?? null)
                                            : null;
                                    const editableVisit =
                                        item.kind === "visit" && item.sourceId
                                            ? (visitsById.get(item.sourceId) ?? null)
                                            : null;
                                    return (
                                        <ArrivingListItem
                                            key={item.id}
                                            isNew={arrivals.has(item.id)}
                                            className="">
                                            {note ? (
                                                <RecentActivityNoteRow
                                                    entityId={entityId}
                                                    refetch={refetch}
                                                    note={note}
                                                    pinnedNoteId={pinnedNoteId}
                                                    onDeleted={handleNoteDeleted}
                                                    onUpdated={handleNoteUpdated}
                                                />
                                            ) : (
                                                <FeedItemRow
                                                    item={item}
                                                    parent={parent}
                                                    onEdit={
                                                        editableTask
                                                            ? () =>
                                                                  setEditingTask(
                                                                      editableTask,
                                                                  )
                                                            : editableVisit
                                                              ? () =>
                                                                    setEditingVisit(
                                                                        editableVisit,
                                                                    )
                                                              : null
                                                    }
                                                    recordType={feedRecordType}
                                                    activityTypes={activityTypes}
                                                    onTypeSaved={handleTypeSaved}
                                                />
                                            )}
                                        </ArrivingListItem>
                                    );
                                })}
                            </AnimatePresence>
                            {filtered.length === 0 ? (
                                <li className="py-2.5">
                                    <BlankSlate>
                                        {items.length === 0
                                            ? "No activity yet."
                                            : "No activity matches the selected types."}
                                    </BlankSlate>
                                </li>
                            ) : null}
                        </ActivityRowsList>
                        {hasMore ? (
                            <InteractiveTextLink
                                className="mt-3 inline-flex items-center gap-1 text-sm font-medium"
                                onClick={() =>
                                    setVisibleCount(c => c + SHOW_MORE_INCREMENT)
                                }>
                                <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
                                Show more
                            </InteractiveTextLink>
                        ) : null}
                        {reachedEnd ? (
                            <p className="mt-3 text-sm text-gray-500">
                                No more activities to show.
                            </p>
                        ) : null}
                        {loggingActivity ? (
                            <LogActivityWizard parent={parent} onClose={closeAction} />
                        ) : null}
                        <ResponsivePanel
                            isOpen={editingTask !== null}
                            type={PanelType.medium}
                            headerText="Edit task"
                            onDismiss={closeEditors}>
                            {editingInitial ? (
                                <TaskFormV2
                                    initial={editingInitial}
                                    mode="update"
                                    onClose={closeEditors}
                                    saveTask={async (_parentId, payload) => {
                                        if (!editingTask) {
                                            return;
                                        }
                                        await updateTask.mutateAsync({
                                            taskId: editingTask.id,
                                            params: payload,
                                        });
                                        closeEditors();
                                        refreshAgenda();
                                    }}
                                    onDelete={async () => {
                                        if (!editingTask) {
                                            return;
                                        }
                                        await deleteTask.mutateAsync({
                                            taskId: editingTask.id,
                                        });
                                        closeEditors();
                                        refreshAgenda();
                                    }}
                                    entityLinkDisabled
                                />
                            ) : null}
                        </ResponsivePanel>
                        {editingVisit ? (
                            <VisitEditor
                                mode="edit"
                                visit={editingVisit}
                                onSaved={() => {
                                    closeEditors();
                                    refreshAgenda();
                                }}
                                onClose={closeEditors}
                            />
                        ) : null}
                    </InputFormBlock>
                );
            }}
        </QueryRenderer>
    );
};
